// src/lib/content/video-catalog.ts
//
// The video library's PUBLIC half: collections and each video's title, blurb, topic,
// YouTube id, length and cover — everything a shelf, a lesson or a wrong-answer card
// shows. Safe in client components.
//
// The worked problems and takeaways are NOT here. They are the soft gate on
// /videos/[collection]/[slug] (all but the first problem need a free account), and a
// client component that imported them shipped every one in a public JS chunk — the gate
// was readable by anyone who opened the bundle. They live in video-library.ts, which is
// server-only; this module must never import it.
//
// Topic is the only join. Video, lesson and practice already share the 30-topic
// vocabulary, so a video declares one topic and everything else follows from it — there
// is no second tagging system to keep in sync. A test pins every topic to the canonical
// list, so a typo fails CI rather than silently orphaning a video.

import type { VideoPractice } from './video-practice'

export type Collection = {
  slug: string
  /**
   * `series` — our own videos, listed in VIDEO_CATALOG, one page each.
   * `curated` — outside YouTube videos we picked (topic-videos.ts), shown on the collection
   * page only: they get no per-video page of ours, and every card names its channel.
   */
  kind: 'series' | 'curated'
  title: string
  /** One line, used on the shelf card. */
  blurb: string
  /** A paragraph, used as the collection page's lede and meta description. */
  description: string
}

export type Problem = { given: string; ask: string; answer: string; how: string }

/** A video without its gated half (video-library.ts adds `problems` and `takeaway`). */
export type VideoSummary = {
  collection: string
  slug: string
  /** Position within its collection, 1-based. */
  n: number
  title: string
  blurb: string
  /**
   * 11-character YouTube id, from the URL after /shorts/ or ?v= — both forms point at
   * the same video. Empty string means "not published yet": the video is hidden from
   * every surface rather than rendering an embed that would 404.
   */
  youtubeId: string
  /** Canonical topic name from src/lib/sat/taxonomy.ts. */
  topic: string
  duration: string
  cover: string
}

export const COLLECTIONS: Collection[] = [
  {
    slug: 'desmos',
    kind: 'series',
    title: 'The Desmos Series',
    blurb: 'Using the graphing calculator that is built into the test.',
    description:
      'The Digital SAT hands you a graphing calculator for the entire Math section, and almost nobody uses it properly. Each episode takes a real question type and solves it visually — in about fifteen seconds instead of ninety.',
  },
  {
    // Built from topic-videos.ts by rw-video-collection.ts. Not ours — the copy says so.
    slug: 'reading-writing',
    kind: 'curated',
    title: 'Reading & Writing',
    blurb: 'Hand-picked walkthroughs from Khan Academy and other channels.',
    description:
      'Free videos from Khan Academy and other SAT channels on every Digital SAT Reading & Writing skill, starting with punctuation, grammar, inferences and transitions. We picked them; we did not make them. Each skill links to our written lesson and to practice questions on it.',
  },
]

export const VIDEO_CATALOG: VideoSummary[] = [
  {
    collection: 'desmos',
    n: 1,
    slug: 'solve-without-solving',
    title: 'Solve any equation without solving it',
    blurb: 'Graph both sides, and the answer is wherever they cross.',
    youtubeId: 'zrKxxWRQDb4',
    topic: 'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
    duration: '2:23',
    cover: '/desmos/ep01.jpg',
  },
  {
    collection: 'desmos',
    n: 2,
    slug: 'make-desmos-try-all-four',
    title: 'Make Desmos try all four answers for you',
    blurb: 'Leave the unknown letter in the equation and drag a slider.',
    youtubeId: '83_ttmkxVjI',
    topic: 'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
    duration: '2:20',
    cover: '/desmos/ep02.jpg',
  },
  {
    collection: 'desmos',
    n: 3,
    slug: 'never-complete-the-square',
    title: 'Never complete the square again',
    blurb: 'Desmos draws equations, not just functions — so type the whole circle.',
    youtubeId: 'rK4dpJSvz_U',
    topic: 'Circles',
    duration: '1:59',
    cover: '/desmos/ep03.jpg',
  },
  {
    collection: 'desmos',
    n: 4,
    slug: 'shade-it',
    title: 'Shade it and the answer shows up',
    blurb: 'Desmos shades inequalities — so plot the answer choices and look.',
    youtubeId: '32J-PvUHtJ8',
    topic: 'Linear Inequalities in 1 or 2 Variables',
    duration: '1:58',
    cover: '/desmos/ep04.jpg',
  },
  {
    collection: 'desmos',
    n: 5,
    slug: 'the-vertex-is-a-point',
    title: 'The vertex is just a point — click it',
    blurb: 'No −b/2a, no substituting back. The answer is a coordinate.',
    youtubeId: 'szCLu__97eA',
    topic: 'Nonlinear Functions',
    duration: '1:49',
    cover: '/desmos/ep05.jpg',
  },
  {
    collection: 'desmos',
    n: 6,
    slug: 'slide-until-parallel',
    title: 'Slide until the lines are parallel',
    blurb: 'Leave the constants in, add sliders, and watch for parallel.',
    youtubeId: 'amMCR1RrxbA',
    topic: 'Systems of 2 Linear Equations in 2 Variables',
    duration: '2:25',
    cover: '/desmos/ep06.jpg',
  },
  {
    collection: 'desmos',
    n: 7,
    slug: 'never-factor-again',
    title: 'Never factor again — graph both and look',
    blurb:
      'Equivalent means the same graph, so graph the ugly one and see which choice lands on top.',
    youtubeId: '7Me5sp3kLLM',
    topic: 'Equivalent Expressions',
    duration: '1:59',
    cover: '/desmos/ep07.jpg',
  },
  {
    collection: 'desmos',
    n: 8,
    slug: 'squaring-invents-answers',
    title: 'Squaring both sides invents answers',
    blurb:
      'Graph both sides: the graph adds no extra answers to throw away. Then check the one it shows.',
    youtubeId: '8lL9kHN4mac',
    topic: 'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
    duration: '1:50',
    cover: '/desmos/ep08.jpg',
  },
  {
    collection: 'desmos',
    n: 9,
    slug: 'leave-the-unknown-in',
    title: 'The list has a variable? Type it anyway',
    blurb: 'Leave the unknown in the data and let Desmos draw the answer.',
    youtubeId: 'MRt2oCwPAAs',
    topic: 'One-Variable Data: Distributions and Measures of Center and Spread',
    duration: '1:46',
    cover: '/desmos/ep09.jpg',
  },
  {
    collection: 'desmos',
    n: 10,
    slug: 'the-table-is-the-equation',
    title: 'Stop eyeballing the line of best fit',
    blurb: 'Put the data in a Desmos table, type a tilde, and Desmos fits the model for you.',
    youtubeId: '-4eDrGd2Ip4',
    topic: 'Two-Variable Data: Models and Scatterplots',
    duration: '2:07',
    cover: '/desmos/ep10.jpg',
  },
  {
    collection: 'desmos',
    n: 11,
    slug: 'growth-rate-without-the-rule',
    title: 'Doubles every 8 years? It is not 12.5%',
    blurb: 'Get the yearly growth rate in one line, then read the year off a crossing.',
    youtubeId: 'ZYPVCV4-GJk',
    topic: 'Nonlinear Functions',
    duration: '1:37',
    cover: '/desmos/ep11.jpg',
  },
  {
    collection: 'desmos',
    n: 12,
    slug: 'type-the-distance',
    title: 'Equidistant? Type it as written',
    blurb: 'Skip the distance formula: type distance() = distance() and Desmos finds the point.',
    youtubeId: 'nWDjxgJ175A',
    topic: 'Coordinate Geometry',
    duration: '1:41',
    cover: '/desmos/ep12.jpg',
  },
  {
    collection: 'desmos',
    n: 13,
    slug: 'mystery-coefficient-slider',
    title: 'A mystery k? Make it a slider',
    blurb:
      'Find a missing coefficient by sliding k, then read the other roots straight off the graph.',
    youtubeId: 'HFX_s-ntj7o',
    topic: 'Nonlinear Functions',
    duration: '1:34',
    cover: '/desmos/ep13.jpg',
  },
  {
    collection: 'desmos',
    n: 14,
    slug: 'triangle-sides-with-x',
    title: 'The sides have x in them? Leave it in',
    blurb:
      'Solve a right triangle whose sides are expressions without the quadratic, then a sine-equals-cosine equation in degree mode.',
    youtubeId: 'kGzerftBUqc',
    topic: 'Right Triangles and Trigonometry',
    duration: '1:41',
    cover: '/desmos/ep14.jpg',
  },
]

/* ------------------------------------------------------------------ */
/*  Lookups                                                            */
/* ------------------------------------------------------------------ */

export const isPublished = (v: Pick<VideoSummary, 'youtubeId'>) => v.youtubeId.length === 11

export const getCollection = (slug: string) => COLLECTIONS.find((c) => c.slug === slug)

export const collectionPath = (c: Pick<Collection, 'slug'>) => `/videos/${c.slug}`
export const videoPath = (v: Pick<VideoSummary, 'collection' | 'slug'>) =>
  `/videos/${v.collection}/${v.slug}`

/**
 * The lookups, over either half: this module runs them over the public catalog,
 * video-library.ts over the full videos — one implementation, so the two can't drift.
 */
export function videoLookups<V extends VideoSummary>(all: readonly V[]) {
  const publishedVideos = () => all.filter(isPublished)

  /** Published videos in a collection, in episode order. */
  const videosInCollection = (collection: string) =>
    publishedVideos()
      .filter((v) => v.collection === collection)
      .sort((a, b) => a.n - b.n)

  return {
    publishedVideos,
    videosInCollection,
    getVideo: (collection: string, slug: string) =>
      all.find((v) => v.collection === collection && v.slug === slug),
    /** Published videos on a canonical topic, case-insensitive, in collection + episode order. */
    videosForTopic: (topic: string) => {
      const t = topic.toLowerCase()
      return publishedVideos()
        .filter((v) => v.topic.toLowerCase() === t)
        .sort((a, b) => a.collection.localeCompare(b.collection) || a.n - b.n)
    },
    /** The video behind a YouTube id — lets a library entry point back at its own page. */
    videoByYouTubeId: (id: string | null | undefined) =>
      id ? publishedVideos().find((v) => v.youtubeId === id) : undefined,
    /** Neighbours within the same collection, for prev/next on the video page. */
    adjacentVideos: (v: Pick<VideoSummary, 'collection' | 'slug'>) => {
      const list = videosInCollection(v.collection)
      const i = list.findIndex((x) => x.slug === v.slug)
      return { prev: i > 0 ? list[i - 1] : undefined, next: i >= 0 ? list[i + 1] : undefined }
    },
  }
}

export const {
  publishedVideos,
  videosInCollection,
  getVideo,
  videosForTopic,
  videoByYouTubeId,
  adjacentVideos,
} = videoLookups(VIDEO_CATALOG)

/** Topics that have at least one published video — the emerald chips on the hub. */
export const topicsWithVideos = () => {
  const m = new Map<string, number>()
  for (const v of publishedVideos()) m.set(v.topic, (m.get(v.topic) ?? 0) + 1)
  return m
}

/** Total runtime of a list of videos as "m:ss" — collection pages show it up top. */
export const totalDuration = (videos: Pick<VideoSummary, 'duration'>[]) => {
  const secs = videos.reduce((acc, v) => {
    const [m, s] = v.duration.split(':').map(Number)
    return acc + (m || 0) * 60 + (s || 0)
  }, 0)
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
}

/** Topic-scoped practice, ten questions — the exit every video page hands out. */
export const practiceHref = (topic: string) =>
  `/practice/session?topic=${encodeURIComponent(topic)}&count=10`

/**
 * What /api/videos/[collection]/[slug] returns to a signed-in browser: everything the static
 * page left out. `problems` are the LOCKED ones only — the free one is already on the page.
 */
export type UnlockedVideo = {
  problems: Problem[]
  takeaway: string
  /** `/learn/<slug>` when that lesson exists, else null — a dead Learn button is worse than none. */
  lessonHref: string | null
  /**
   * The Practise button: questions picked for this video's trick, or labelled topic
   * practice (video-practice.ts). Null when the topic has nothing to practise.
   */
  practice: VideoPractice | null
}
